import { Flag } from "@/flags";
import { router } from "@/lib/router";
import { resolveAttachmentUrl } from "@/lib/utils/attachment-url-resolver";
import { DateUtils } from "@/lib/utils/date";
import { Format } from "@/lib/utils/format";
import { PageContentViewModel } from "@/lib/vm/page-content-viewmodel";
import { requestContext } from "@/stores/common/request-context";
import reactTemplate from "./home-react.pug";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { PageCursor } = require("@/lib/utils/page-cursor");

// Auth, Real-Time & Stores
import { authManager } from "@/lib/managers/auth-manager";
import { AuthManager2 } from "@/lib/managers/auth-manager-2";
import { fanoutManager, FanoutManager } from "@/lib/managers/fanout-manager";
import { defaultStoreCore as defaultStore } from "@/stores/default-store.core";
import { NotificationStore } from "@/stores/notification-store.core";
import { PersonStore } from "@/stores/person-store.core";

// Mediators
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { PageableLoadingMediator } = require("@/lib/mediators/pageable-loading-mediator");

// Models
import { PermissionLevel } from "@/models/permission-level";
// eslint-disable-next-line @typescript-eslint/no-var-requires
import { Notification } from "@/models/notification";

// Framework Includes
import ko from "knockout";

import renderReactComponent from "@/react/render-react-component";

const SHORT_FORMAT_OPTIONS = {
   yearFormat: DateUtils.YearFormat.FULL,
   monthFormat: DateUtils.MonthFormat.ABBREV,
   dayFormat: DateUtils.DayFormat.ONE_DIGIT,
   weekdayFormat: DateUtils.WeekDayFormat.ABBREV,
};
const ITEMS_PER_REQUEST = 50;
const PRIVACY_SETTINGS_CONFIRMED = "privacy-settings-confirmed";

export class HomeViewModel extends PageContentViewModel {
   public enableEmbeddedAppUsers: boolean;
   public canViewPeople: any;
   public canViewProject: any;
   public companyName: ko.Observable<string | undefined>;
   public logoUrl: ko.Observable<string | null>;
   public showLogo: ko.PureComputed<boolean>;
   public cursor: any; // PageCursor
   public currentRequest: ko.Observable<any>;
   public progressMediator: any;
   public notifications: ko.ObservableArray<any>; // Notification[]
   public groupCount: ko.Observable<string>;
   public peopleCount: ko.Observable<string>;
   public projectCount: ko.Observable<string>;
   public hasUnreadNotifications: ko.PureComputed<boolean>;
   public reactRoot: any;

   constructor() {
      super(reactTemplate(), "Home");
      this.reactRoot = null;
      this.enableEmbeddedAppUsers = requestContext.usingProcoreHostApp;
      this.companyName = ko.observable<string | undefined>();
      if (Flag.ENABLE_WORKFORCE_PLANNING_V1 || requestContext.usingProcoreHostApp) {
         this.logoUrl = ko.observable<string | null>(null);
      } else {
         this.logoUrl = ko.observable<string | null>(
            `${(window as any).config.LC_DOMAIN}/images/lc-logo-vert.png`,
         );
      }
      this.showLogo = ko.pureComputed(() => {
         return this.logoUrl() != null;
      });
      this.currentRequest = ko.observable<any>();
      this.notifications = ko.observableArray<any>();
      this.groupCount = ko.observable<string>("...");
      this.peopleCount = ko.observable<string>("...");
      this.projectCount = ko.observable<string>("...");
      this.hasUnreadNotifications = ko.pureComputed(() => {
         return this.notifications().filter((item: any) => !item.viewed()).length > 0;
      });
      // Permissions
      this.canViewPeople = authManager.checkAuthAction(PermissionLevel.Action.VIEW_PEOPLE);
      this.canViewProject = authManager.checkAuthAction(PermissionLevel.Action.VIEW_PROJECT);

      this.cursor = this.getNewCursor();

      this.progressMediator = new PageableLoadingMediator(() => {
         this.refresh({ skip: 0 });
      });

      fanoutManager.getSubscription(
         null,
         "vm.HomeViewModel",
         FanoutManager.Channel.USERS_NOTIFICATIONS,
         null,
         () => this.refresh({ skip: 0 }),
      );

      this.loadData();
   }

   public getMetaString(notification: any): string {
      const createdAt = new Date(notification.createdAt());
      const date = DateUtils.formatDate(createdAt);
      const time = DateUtils.getTime(createdAt);
      return `${notification.authorName()} - ${date} at ${time}`;
   }

   public getActionColorClass(notification: Notification): string {
      switch (notification.action()) {
         case Notification.Action.CREATE:
            return "home-page__notifications-row__action-dot--green";
         case Notification.Action.UPDATE:
            return "home-page__notifications-row__action-dot--blue";
         case Notification.Action.DELETE:
            return "home-page__notifications-row__action-dot--red";
         case Notification.Action.WARNING:
            return "home-page__notifications-row__action-dot--yellow";
         default:
            return "";
      }
   }

   public maybeGetDayBreakString(
      notification: any,
      index: number | ko.Observable<number>,
   ): string | null {
      const unwrappedIndex = ko.unwrap(index);
      if (unwrappedIndex == 0) {
         return DateUtils.formatDetachedDay(
            new Date(notification.createdAt()),
            defaultStore.getDateFormat(),
            SHORT_FORMAT_OPTIONS,
         );
      } else {
         const priorDate = new Date(this.notifications()[unwrappedIndex - 1].createdAt()).getDate();
         const currentDate = new Date(notification.createdAt()).getDate();
         if (priorDate != currentDate) {
            return DateUtils.formatDetachedDay(
               new Date(notification.createdAt()),
               defaultStore.getDateFormat(),
               SHORT_FORMAT_OPTIONS,
            );
         } else {
            return null;
         }
      }
   }

   public async navigateToNotification(notification: any): Promise<void> {
      // Need to use my-groups instead of the last viewed Group from auth.
      // This will handle clicking into a notification where the entity
      // does not belong to the last viewed Group.
      const MY_GROUPS = "my-groups";

      // TODO: Save cursor location for resumption.
      try {
         await NotificationStore.notificationViewed(notification.id).payload;
      } catch (error) {
         console.log("Error: ", error);
         return;
      }

      if (notification.entity() === Notification.Entity.PERSON) {
         router.navigate(null, `/groups/${MY_GROUPS}/people/${notification.subject1Id()}`);
      } else if (notification.entity() === Notification.Entity.PROJECT) {
         router.navigate(null, `/groups/${MY_GROUPS}/projects/${notification.subject1Id()}`);
      } else if (
         notification.entity() === Notification.Entity.ASSIGNMENT ||
         notification.entity() === Notification.Entity.REQUEST
      ) {
         const filterData = encodeURIComponent(
            JSON.stringify({
               value: notification.subject1Id(),
               valueName: notification.subject1Name(),
               filterName: "Project",
               property: "id",
            }),
         );
         let url = `/groups/${MY_GROUPS}/gantt?notificationFilter=${filterData}`;
         if (notification.meta()?.start_day) {
            const startDay = notification.meta().start_day;
            url += `&dayFilter=${startDay}`;
         }
         router.navigate(null, url);
      }
   }

   public async clearAllNotifications(): Promise<void> {
      try {
         const success = await NotificationStore.clearAllNotifications().payload;
         if (success) {
            for (const item of this.notifications()) {
               item.viewed(true);
            }
         }
      } catch (error) {
         console.log("Error: ", error);
         return;
      }
   }

   public getNewCursor(): any {
      return new PageCursor({
         cacheId: "home",
         defaultLimit: ITEMS_PER_REQUEST,
         sizeOf: (data: any[]) => data.length,
         loader: async (params: any, callback: (err: any, data?: any[]) => void) => {
            try {
               const notifications = await NotificationStore.getNotifications(params).payload;
               // TODO: Use notification-serializer instead of new Notification
               const data = notifications.data.map((item: any) => new Notification(item));
               return callback(null, data);
            } catch (err) {
               console.log("Error: ", err);
               return callback(err);
            }
         },
      });
   }

   public onReachedBottom(): void {
      this.loadBottomRecords();
   }

   public loadBottomRecords(): void {
      if (this.currentRequest() || !this.cursor.hasMoreBottomRecords()) {
         return;
      }
      this.progressMediator.showLoader();
      this.progressMediator.startProgress();

      this.currentRequest(
         this.cursor.increment({}, (err: any, data: any[]) => {
            if (err) {
               // TODO: Show error.
               console.log("Error: ", err);
               return;
            }
            this.progressMediator.appendData(() => {
               this.currentRequest(null);
               ko.utils.arrayPushAll(this.notifications, data);
            });
         }),
      );
   }

   public refresh(overrides: { skip?: number } = {}): void {
      if (this.currentRequest()) {
         this.currentRequest().cancel();
         this.currentRequest(null);
      }

      if (overrides.skip !== undefined && overrides.skip !== null) {
         this.cursor.startingIndex(overrides.skip);
      }

      this.notifications([]);
      this.loadBottomRecords();
   }

   public async loadData(): Promise<void> {
      try {
         const result = await PersonStore.getUserHomeInfo().payload;
         const data = result.data;

         // Please leave this.
         // It's required to confirm the embedded experience can load in Firefox and Safari
         if (requestContext.usingProcoreHostApp) {
            window.localStorage.setItem(PRIVACY_SETTINGS_CONFIRMED, "true");
         }

         this.companyName(data.companyName);
         if (data.logoUrl) {
            const resolved = await resolveAttachmentUrl(data.logoUrl);
            this.logoUrl(resolved.url);
         }

         if (data.groupCount) {
            this.groupCount(Format.formatNumber(data.groupCount));
         }

         if (data.peopleCount) {
            this.peopleCount(Format.formatNumber(data.peopleCount));
         }

         if (data.projectCount) {
            this.projectCount(Format.formatNumber(data.projectCount));
         }
      } catch (error) {
         console.log("Error getHomeInfo: ", error);
      }
   }

   public signout(): void {
      const executeSignout = () => {
         authManager.logout(() => {
            AuthManager2.navigateToSignIn();
         });
      };

      const curChild = this.getChild("main");
      if (curChild && typeof (curChild as any).dispose === "function") {
         (curChild as any).dispose(executeSignout);
      } else {
         executeSignout();
      }
   }

   public dispose(next: () => void): void {
      fanoutManager.unsubscribe("vm.HomeViewModel", FanoutManager.Channel.USERS_NOTIFICATIONS);
      if (this.cursor) {
         this.cursor.dispose();
      }

      next();
   }

   public addedToParent(): void {
      this.reactRoot = renderReactComponent("home-mount", "HomeContainer", {});
   }

   public removedFromParent(): void {
      if (this.reactRoot) {
         this.reactRoot.unmount();
         this.reactRoot = null;
      }
   }
}
